import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { Route42Markdown } from '../../../components/ui/Route42Markdown';
import rawCatalog from '../data/help-content.v1.json';
import type { HelpContentCatalog } from '../types/product-guidance';
import { areaLabel, helpIcon } from './HelpCenterPage';
import { recordProductGuidanceEventBestEffort } from '../api/product-guidance-analytics.api';
import type { HelpFeedbackReason } from '../types/product-guidance';

const catalog = rawCatalog as HelpContentCatalog;

export function HelpArticlePage() {
  const { articleId = '' } = useParams();
  const article = catalog.articles.find((item) => item.id === articleId);

  useEffect(() => {
    if (!article) return;
    recordProductGuidanceEventBestEffort({ event_type: 'HELP_ARTICLE_VIEW', article_id: article.id });
  }, [article]);

  if (!article) return <Navigate to="/help" replace />;
  const related = catalog.articles.filter((item) => item.id !== article.id && (item.product_area === article.product_area || item.tasks.some((task) => article.tasks.includes(task)))).slice(0, 4);

  return (
    <section className="r42-help-article-page">
      <div className="r42-help-article-main">
        <Link className="r42-back-link" to="/help"><i className="bi bi-arrow-left" aria-hidden="true" /> Help Center</Link>
        <header className="r42-help-article-head">
          <span className="r42-help-article-topic"><i className={helpIcon(article.product_area)} aria-hidden="true" /> {areaLabel(article.product_area)}</span>
          <h1>{article.title}</h1>
          <p>{article.summary}</p>
        </header>
        <article className="r42-help-article-content"><Route42Markdown content={article.body} /></article>
        <div className="r42-help-article-confidence"><i className="bi bi-check-circle" aria-hidden="true" /><div><strong>Route42 product guidance</strong><span>Reviewed {formatDate(article.last_reviewed)} · This guide is part of the same approved knowledge Ace uses.</span></div></div>
        <HelpArticleFeedback articleId={article.id} />
      </div>
      {related.length ? <aside className="r42-help-related"><span className="r42-eyebrow">Related help</span>{related.map((item) => <Link key={item.id} to={`/help/${item.id}`}>{item.title}<i className="bi bi-arrow-right" aria-hidden="true" /></Link>)}</aside> : null}
    </section>
  );
}

function formatDate(value: string) {
  const date = new Date(`${value}T00:00:00Z`);
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date);
}

function HelpArticleFeedback({ articleId }: { articleId: string }) {
  const [rating, setRating] = useState<'HELPFUL' | 'NOT_HELPFUL' | null>(null);
  const [reason, setReason] = useState<HelpFeedbackReason | null>(null);

  function rate(next: 'HELPFUL' | 'NOT_HELPFUL') {
    setRating(next);
    setReason(null);
    recordProductGuidanceEventBestEffort({ event_type: 'HELP_ARTICLE_FEEDBACK', article_id: articleId, rating: next });
  }

  function explain(next: HelpFeedbackReason) {
    setReason(next);
    recordProductGuidanceEventBestEffort({ event_type: 'HELP_ARTICLE_FEEDBACK', article_id: articleId, rating: 'NOT_HELPFUL', reason: next });
  }

  return (
    <section className="r42-help-feedback" aria-label="Help article feedback">
      {rating === null ? (
        <>
          <strong>Was this guide helpful?</strong>
          <div>
            <button type="button" onClick={() => rate('HELPFUL')}><i className="bi bi-hand-thumbs-up" aria-hidden="true" /> Yes</button>
            <button type="button" onClick={() => rate('NOT_HELPFUL')}><i className="bi bi-hand-thumbs-down" aria-hidden="true" /> No</button>
          </div>
        </>
      ) : rating === 'HELPFUL' ? (
        <span><i className="bi bi-check-circle" aria-hidden="true" /> Thanks. That helps us keep Route42 guidance useful.</span>
      ) : reason ? (
        <span><i className="bi bi-check-circle" aria-hidden="true" /> Thanks. We'll use that signal to improve this guide.</span>
      ) : (
        <>
          <strong>What could be better?</strong>
          <div className="r42-help-feedback__reasons">
            <button type="button" onClick={() => explain('DID_NOT_FIND_ANSWER')}>Didn't answer what I needed</button>
            <button type="button" onClick={() => explain('NOT_CLEAR')}>Not clear</button>
            <button type="button" onClick={() => explain('OUTDATED_OR_INCORRECT')}>Seems outdated or incorrect</button>
            <button type="button" onClick={() => explain('STEPS_DID_NOT_MATCH')}>Steps didn't match</button>
          </div>
        </>
      )}
    </section>
  );
}
